import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin, Users, Clock, Zap, CheckCircle2, Target, Trophy, Activity, ArrowLeft } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import SchemaMarkup from "@/components/site/SchemaMarkup";
import WhatsAppButton from "@/components/site/WhatsAppButton";
import Footer from "@/components/site/Footer";
import ClinicRegister from "@/components/site/ClinicRegister";

const CURRICULUM = [
  { week: 1, title: "Build Your Foundation", icon: Target, items: [
    "Initial movement assessment",
    "Posture and movement mechanics",
    "Shoulder and thoracic mobility",
    "Core stability",
    "Foundational strength exercises",
    "Baseline measurements",
  ]},
  { week: 2, title: "Develop Your Pulling Power", icon: Zap, items: [
    "Scapular mechanics",
    "Upper-back and lat strength",
    "Seated rows and lat pulldowns",
    "Single-arm pulling",
    "Grip strength and endurance",
  ]},
  { week: 3, title: "Upper Body Strength & Shoulder Stability", icon: Activity, items: [
    "Pressing mechanics",
    "Chest and triceps development",
    "Shoulder stability",
    "Rotator cuff strengthening",
    "Balanced upper-body development",
  ]},
  { week: 4, title: "Rotation, Mobility & Joint Function", icon: Activity, items: [
    "Thoracic spine mobility",
    "Controlled trunk rotation",
    "Shoulder and hip mobility",
    "Rotational strength",
    "Anti-rotation and core stability",
  ]},
  { week: 5, title: "Power, Core & Athletic Performance", icon: Zap, items: [
    "Rotational power",
    "Medicine ball training",
    "Unilateral strength",
    "Core integration",
    "Athletic conditioning",
    "Transfer strength into paddling performance",
  ]},
  { week: 6, title: "Test, Refine & Perform", icon: Trophy, items: [
    "Repeat selected baseline assessments",
    "Compare progress",
    "Review exercise technique",
    "Individual performance recommendations",
    "Next-step training roadmap",
    "Certificate of completion",
  ]},
];

const BENEFITS = [
  { icon: "01", title: "Paddler-Specific", body: "Every drill chosen to transfer directly to the stroke — not generic gym work." },
  { icon: "02", title: "Small Cohort", body: "Max 20 athletes so Coach JC sees your form on every rep." },
  { icon: "03", title: "Measurable Progress", body: "Week 1 baseline → Week 6 retest. Numbers you can trust." },
  { icon: "04", title: "Certificate", body: "Completion certificate at Week 6 + personalized next-step roadmap." },
];

const FAQ = [
  { q: "Do I need to be a competitive paddler?", a: "No. The clinic is designed for recreational and competitive paddlers who want to get stronger, move better, and reduce injury risk. All experience levels welcome." },
  { q: "What should I bring?", a: "Comfortable training clothes, running or cross-training shoes, a water bottle, and a notepad or phone to log your weekly numbers." },
  { q: "What if I miss a session?", a: "The curriculum builds week over week, so showing up matters. Missed sessions aren't refundable, but Coach JC will share a catch-up video so you can stay on track." },
  { q: "Is this a medical rehab program?", a: "No. This is an introductory educational and physical training clinic. It is not a medical rehabilitation service and does not guarantee improved race results." },
  { q: "What's included in the price?", a: "All 6 sessions, movement assessment, personalized recommendations, printed workout log, and your completion certificate. Taxes and processing fee are added at checkout and shown transparently." },
];

export default function PowerThePaddle() {
  const { slug = "power-the-paddle" } = useParams();
  const [clinic, setClinic] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    api.get(`/clinics/${slug}`)
      .then((r) => { setClinic(r.data); setError(null); })
      .catch((e) => setError(e.response?.status === 404 ? "Clinic not found." : "Could not load clinic."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#050505] text-white/40 font-mono-accent text-xs">Loading…</div>;
  if (error) return (
    <div className="min-h-screen grid place-items-center bg-[#050505] text-white px-6 text-center">
      <div>
        <p className="font-mono-accent text-[10px] text-[#005EEA] mb-3">— ERROR</p>
        <h1 className="font-display text-4xl uppercase mb-4">{error}</h1>
        <Link to="/" className="font-mono-accent text-xs text-[#005EEA] hover:underline">← Back to home</Link>
      </div>
    </div>
  );

  const isFull = clinic.available <= 0;
  const lowAvailable = clinic.available > 0 && clinic.available <= 3;
  const startDate = new Date(clinic.start_date + "T00:00:00");
  const startFmt = startDate.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });
  const endDate = new Date(clinic.end_date + "T00:00:00");
  const endFmt = endDate.toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="bg-[#050505] text-white min-h-screen" data-testid="clinic-page">
      <SchemaMarkup />

      {/* HERO */}
      <section id="clinic-hero" className="relative min-h-[100svh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#001033] to-[#050505]" />
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1540539234-c14a20fb7c7b?w=1600&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-[#005EEA]/20 rounded-full blur-3xl pointer-events-none" />

        <Link to="/" className="absolute top-6 left-6 sm:left-10 z-20 inline-flex items-center gap-2 text-white/60 hover:text-white font-mono-accent text-[10px] uppercase tracking-[0.2em] transition-colors" data-testid="clinic-back-link">
          <ArrowLeft size={14} /> Getfitwithjeanclaude
        </Link>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-24 min-h-[100svh] flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
          >
            <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-6">
              — ALKAME × GETFITWITHJEANCLAUDE PARTNERSHIP
            </div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-9xl leading-[0.85] uppercase mb-6">
              Power the<br/><span className="text-[#005EEA]">Paddle.</span>
            </h1>
            <p className="font-mono-accent text-sm sm:text-base text-white/80 tracking-wide mb-4">
              PADDLER PERFORMANCE LAB · 6 WEEKS · STARTS {startFmt.toUpperCase()}
            </p>
            <p className="text-base sm:text-xl text-white/70 max-w-2xl leading-relaxed mb-10">
              Build strength. Move better. Paddle stronger. A six-week strength & conditioning clinic
              designed specifically for paddlers, in a cohort of just <strong className="text-white">20 athletes</strong>.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={() => setRegisterOpen(true)}
                disabled={isFull}
                data-testid="clinic-hero-register-btn"
                className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white px-8 py-4 hover:bg-[#0047b3] transition-colors shadow-[0_0_40px_rgba(0,94,234,0.5)] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFull ? "Fully Booked — Join Waitlist" : "Register Now"} <ArrowRight size={13} />
              </button>
              <a href="#curriculum" className="font-mono-accent text-xs uppercase tracking-[0.2em] border border-white/20 text-white px-8 py-4 hover:border-white hover:bg-white/5 transition-colors">
                See 6-Week Curriculum
              </a>
            </div>

            {/* Availability indicator */}
            <div className="mt-10 inline-flex items-center gap-3 border border-white/15 bg-black/40 backdrop-blur-sm px-5 py-3" data-testid="clinic-availability">
              <span className={`w-2 h-2 rounded-full ${isFull ? "bg-red-500" : lowAvailable ? "bg-yellow-400 animate-pulse" : "bg-green-400"}`} />
              <span className="font-mono-accent text-[11px] text-white/80">
                {isFull ? "COHORT FULL — WAITLIST OPEN" : `${clinic.available} OF ${clinic.capacity} SPOTS REMAINING`}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PARTNERSHIP INTRO */}
      <section className="py-20 sm:py-28 bg-[#080808] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-4">— THE PARTNERSHIP</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9] mb-8">
              Two experts.<br/><span className="text-[#005EEA]">One mission.</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-10 mt-10">
              <div className="border border-white/10 bg-black/40 p-8">
                <p className="font-mono-accent text-[10px] text-[#005EEA] mb-3">PADDLING EXPERTISE</p>
                <h3 className="font-display text-3xl uppercase mb-4">ALKAME / PaddleZone</h3>
                <p className="text-white/70 leading-relaxed">
                  The paddling home-base — bringing deep technical knowledge of the stroke,
                  the equipment, and the community that drives paddlers forward.
                </p>
              </div>
              <div className="border-2 border-[#005EEA] bg-[#005EEA]/[0.08] p-8 shadow-[0_0_40px_rgba(0,94,234,0.15)]">
                <p className="font-mono-accent text-[10px] text-[#005EEA] mb-3">STRENGTH & CONDITIONING</p>
                <h3 className="font-display text-3xl uppercase mb-4">Getfitwithjeanclaude</h3>
                <p className="text-white/70 leading-relaxed">
                  Specialized athlete development — professional strength and conditioning
                  programming built for paddlers to improve physical preparation,
                  movement quality, strength, and performance.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY S&C MATTERS */}
      <section className="py-20 sm:py-28 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-4">— WHY IT MATTERS</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9] mb-6">
              Paddling is a <span className="text-[#005EEA]">strength sport.</span>
            </h2>
            <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-3xl">
              Every stroke is a controlled, repeated expression of force through your shoulders,
              lats, and trunk. Specific strength and conditioning builds the raw material your
              technique has to work with — and reduces the shoulder and lower-back injuries that
              end seasons early.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mt-14">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05}>
                <div className="border border-white/10 bg-[#080808] p-6 h-full">
                  <div className="font-mono-accent text-[10px] text-[#005EEA] tracking-[0.2em] mb-3">— {b.icon}</div>
                  <h4 className="font-display text-xl uppercase leading-tight mb-3">{b.title}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6-WEEK CURRICULUM */}
      <section id="curriculum" className="py-20 sm:py-28 bg-[#080808] border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
              <div>
                <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-4">— SIX-WEEK CURRICULUM</div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9]">
                  Progressive. Measurable.<br/><span className="text-[#005EEA]">On the water.</span>
                </h2>
              </div>
            </div>
          </Reveal>
          <div className="space-y-3">
            {CURRICULUM.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.week} delay={i * 0.03}>
                  <div className="border border-white/10 bg-black/40 p-6 lg:p-8 hover:border-[#005EEA]/40 transition-colors" data-testid={`week-${w.week}`}>
                    <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
                      <div className="flex items-center gap-4 md:flex-col md:items-start md:min-w-[140px]">
                        <Icon className="text-[#005EEA] shrink-0" size={28} />
                        <div>
                          <div className="font-mono-accent text-[10px] text-white/40 tracking-[0.2em]">WEEK {w.week}</div>
                          <div className="font-display text-xl md:text-2xl uppercase leading-tight mt-1">{w.title}</div>
                        </div>
                      </div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/75">
                        {w.items.map((it) => (
                          <li key={it} className="flex gap-2">
                            <CheckCircle2 size={14} className="text-[#005EEA] shrink-0 mt-1" strokeWidth={2.5} />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-8 text-white/40 text-xs font-mono-accent border border-white/10 bg-black/40 p-4">
              Important: This is an introductory educational and physical training clinic.
              It is not a medical rehabilitation service and does not guarantee improved race results.
            </p>
          </Reveal>
        </div>
      </section>

      {/* DETAILS / PRICING */}
      <section className="py-20 sm:py-28 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-4">— WHAT YOU GET</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9] mb-10">
              Program <span className="text-[#005EEA]">details.</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex gap-4 items-start">
                  <Calendar className="text-[#005EEA] mt-1 shrink-0" size={22} />
                  <div>
                    <div className="font-mono-accent text-[10px] text-white/40 tracking-[0.2em]">DATES</div>
                    <div className="text-white text-base">{startFmt} → {endFmt}</div>
                    <div className="text-white/60 text-sm">Every {clinic.day_of_week} · {clinic.time_slot}</div>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <MapPin className="text-[#005EEA] mt-1 shrink-0" size={22} />
                  <div>
                    <div className="font-mono-accent text-[10px] text-white/40 tracking-[0.2em]">LOCATION</div>
                    <div className="text-white text-base">{clinic.location_name}</div>
                    <div className="text-white/60 text-sm">{clinic.location_address || "Address confirmed on registration"}</div>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <Users className="text-[#005EEA] mt-1 shrink-0" size={22} />
                  <div>
                    <div className="font-mono-accent text-[10px] text-white/40 tracking-[0.2em]">COHORT</div>
                    <div className="text-white text-base">{clinic.capacity} athletes max</div>
                    <div className="text-white/60 text-sm">Small group for maximum coaching attention</div>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <Clock className="text-[#005EEA] mt-1 shrink-0" size={22} />
                  <div>
                    <div className="font-mono-accent text-[10px] text-white/40 tracking-[0.2em]">DURATION</div>
                    <div className="text-white text-base">{clinic.duration_weeks} weeks · 1 hour per session</div>
                  </div>
                </div>
              </div>
              <div className="border-2 border-[#005EEA] bg-[#005EEA]/[0.08] p-8 shadow-[0_0_50px_rgba(0,94,234,0.2)]">
                <div className="font-mono-accent text-[10px] text-[#005EEA] tracking-[0.2em] mb-3">PROGRAM FEE</div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-display text-6xl">{formatPrice(clinic.price_cents, clinic.currency)}</span>
                  <span className="text-white/50 text-sm font-mono-accent">/ ATHLETE</span>
                </div>
                <div className="space-y-2 text-sm text-white/70 border-t border-white/10 pt-4" data-testid="price-breakdown">
                  <div className="flex justify-between"><span>Clinic fee</span><span>{formatPrice(clinic.price_cents, clinic.currency)}</span></div>
                  <div className="flex justify-between"><span>HST (13%)</span><span>{formatPrice(clinic.hst_cents, clinic.currency)}</span></div>
                  <div className="flex justify-between"><span>Processing fee</span><span>{formatPrice(clinic.fee_cents, clinic.currency)}</span></div>
                  <div className="flex justify-between border-t border-white/15 pt-2 mt-2 text-white font-semibold">
                    <span>Total at checkout</span><span>{formatPrice(clinic.total_cents, clinic.currency)}</span>
                  </div>
                </div>
                <button
                  onClick={() => setRegisterOpen(true)}
                  disabled={isFull}
                  data-testid="clinic-details-register-btn"
                  className="mt-6 w-full font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white py-4 hover:bg-[#0047b3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isFull ? "Join Waitlist" : "Secure Your Spot"} <ArrowRight size={13} />
                </button>
                <p className="text-[10px] text-white/40 mt-3 text-center font-mono-accent">
                  Payment by Square · Apple Pay · Google Pay · Afterpay
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 bg-[#080808] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-4">— QUESTIONS</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9] mb-12">
              Common <span className="text-[#005EEA]">asks.</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.03}>
                <details className="group py-6" data-testid={`faq-${i}`}>
                  <summary className="font-display text-xl md:text-2xl uppercase cursor-pointer list-none flex justify-between items-center gap-4">
                    {f.q}
                    <span className="text-[#005EEA] font-mono-accent text-sm group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-white/70 mt-4 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28 bg-[#050505] border-t border-white/5 relative overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#005EEA]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-5">— JOIN THE COHORT</div>
            <h2 className="font-display text-4xl sm:text-6xl uppercase leading-[0.9] mb-6">
              {clinic.available} spots<br/><span className="text-[#005EEA]">left.</span>
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-10">
              Secure your spot — the first cohort is capped at 20 athletes.
            </p>
            <button
              onClick={() => setRegisterOpen(true)}
              disabled={isFull}
              data-testid="clinic-final-register-btn"
              className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white px-10 py-5 hover:bg-[#0047b3] transition-colors shadow-[0_0_50px_rgba(0,94,234,0.5)] disabled:opacity-50 flex items-center gap-2 mx-auto"
            >
              {isFull ? "Join Waitlist" : "Register Now"} <ArrowRight size={13} />
            </button>
          </Reveal>
        </div>
      </section>

      <Footer />

      {/* STICKY REGISTER BUTTON — MOBILE */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden z-40 bg-[#050505] border-t border-white/10 p-3">
        <button
          onClick={() => setRegisterOpen(true)}
          disabled={isFull}
          data-testid="clinic-sticky-register-btn"
          className="w-full font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white py-4 hover:bg-[#0047b3] transition-colors disabled:opacity-50"
        >
          {isFull ? "Join Waitlist" : `Register — ${formatPrice(clinic.total_cents, clinic.currency)}`}
        </button>
      </div>

      <WhatsAppButton />

      <ClinicRegister
        open={registerOpen}
        onClose={() => { setRegisterOpen(false); load(); }}
        clinic={clinic}
        slug={slug}
      />
    </div>
  );
}
